/**
 * Office location defaults + per-tenant resolver.
 *
 * Drives:
 *   - The "office" pin + tooltip on the Live Map View (OfficeMarker)
 *   - Default map center when the map first opens (DEFAULT_MAP_CENTER)
 *   - Fallback coordinates applied to jobs that lack geocoded lat/lng
 *     (JOB_FALLBACK_COORDS, used by useJobs/useMapJobs/several services)
 *
 * Historical context (SaaS-readiness audit, May 2026):
 * The previous defaults pinned to Techery's Bowral head office —
 * "The Techery Southern Highlands, The Mill, Shop 5, 210-224 Bong Bong St,
 * Bowral NSW 2576" at lat -34.4763467, lng 150.4185626. For SaaS each
 * tenant has their own office; the map tooltip is customer-facing on
 * shared screens so a non-Techery tenant can't ship with Techery's name +
 * address visible. This module is the single source of truth going
 * forward.
 *
 * TODO(saas-readiness): callers should pass `tenant` so overrides fire.
 * Today most callers still consume the DEFAULT_* exports directly because
 * they lack tenant context (services without React context). A follow-up
 * commit will thread tenant into each call site (or move services to
 * read getTenantIdSync()-style synchronous tenant access).
 */
import type { TenantConfig } from '@/types/tenant.types';

export interface OfficeLocation {
  lat: number;
  lng: number;
  /** Display name shown on the map office marker tooltip. */
  name: string;
  /** Single-line address shown beneath the name. */
  address: string;
}

/**
 * Default office location — Techery's Bowral head office. Used when a
 * tenant has no override in `tenant.business.officeLocation`.
 */
export const DEFAULT_OFFICE_LOCATION: OfficeLocation = {
  lat: -34.4763467,
  lng: 150.4185626,
  name: 'The Techery Southern Highlands',
  address: 'The Mill, Shop 5, 210-224 Bong Bong St, Bowral NSW 2576',
};

/**
 * Resolve the office location for a tenant. Missing tenant or missing
 * officeLocation returns the default. Partial overrides fall back to the
 * default field-by-field so a tenant can override e.g. lat/lng without
 * also having to re-specify name/address.
 */
export function getOfficeLocation(
  tenant: TenantConfig | null | undefined,
): OfficeLocation {
  const override = tenant?.business?.officeLocation;
  if (!override) return DEFAULT_OFFICE_LOCATION;
  return {
    lat: override.lat ?? DEFAULT_OFFICE_LOCATION.lat,
    lng: override.lng ?? DEFAULT_OFFICE_LOCATION.lng,
    name: override.name ?? DEFAULT_OFFICE_LOCATION.name,
    address: override.address ?? DEFAULT_OFFICE_LOCATION.address,
  };
}

/**
 * Resolve the default map center (lat/lng pair only) for a tenant.
 * Convenience wrapper for callers that don't need the office name/address.
 */
export function getDefaultMapCenter(
  tenant: TenantConfig | null | undefined,
): { lat: number; lng: number } {
  const loc = getOfficeLocation(tenant);
  return { lat: loc.lat, lng: loc.lng };
}

/**
 * Resolve fallback coordinates for jobs that lack geocoded lat/lng.
 * Currently identical to the office location, but kept as a separate
 * helper so a future tenant config could decouple "office pin" from
 * "where ungeocoded jobs stack on the map".
 */
export function getJobFallbackCoords(
  tenant: TenantConfig | null | undefined,
): { lat: number; lng: number } {
  return getDefaultMapCenter(tenant);
}
